import { useMemo, useState } from 'react';
import PosSubscribeModal from './PosSubscribeModal';
import { useConfig } from '../context/ConfigContext';
import { usePosSession } from '../context/PosSessionContext';
import { formatMoney } from '../utils/currencyUtil';
import { formatPlanDateInZone, getSubscriptionState } from '../utils/subscriptionDateUtil';
import { useClientSubscription } from '../hooks/useClientSubscription';
import {
  POS_APPLICATION_STATUS,
  POS_SUBSCRIPTION_STATUS,
  POS_SUBSCRIPTION_REVIEW_WINDOW,
  POS_SUBSCRIPTION_URGENT_DAYS,
} from '../constants/posConst';
import '../css/PosSubscription.css';

/**
 * The strip across the top of the till that tells the club where its
 * subscription stands, and lets it pay for the next period.
 *
 * Shown only when there is something to act on: the plan ends within
 * POS_SUBSCRIPTION_WARN_DAYS, it has already ended, an application is waiting
 * for review, or the last one was rejected. A healthy subscription, and a
 * client an administrator has not enrolled at all, show nothing.
 *
 * AN EXPIRED PLAN DOES NOT LOCK THE TILL. The banner turns red and stays; an
 * administrator decides separately whether to stop a client that does not pay.
 *
 * The Subscribe button is offered to staff who can change settings (the
 * manageConfig permission, or an unrestricted account). A cashier sees the
 * notice and is asked to pass it on, rather than being shown a payment screen
 * for money that is not theirs to spend.
 *
 * @returns {JSX.Element|null}
 */
function PosSubscriptionBanner() {
  const { config, client, clientId } = useConfig();
  const { can } = usePosSession();
  const { subscription, applications } = useClientSubscription(clientId);
  const [open, setOpen] = useState(false);

  const state = useMemo(
    () => getSubscriptionState(subscription || null, client?.timezone),
    [subscription, client?.timezone],
  );

  // Not loaded yet — say nothing rather than flash a banner that disappears.
  if (subscription === undefined) return null;

  const pending = applications.find((application) => application.status === POS_APPLICATION_STATUS.PENDING);
  const latest = applications[0];
  // A rejection stops mattering once an administrator has since set the plan
  // by hand, so it is only reported while it is the newest word on the matter.
  const rejected = !pending
    && latest?.status === POS_APPLICATION_STATUS.REJECTED
    && (latest.reviewedAt || 0) > (Date.parse(subscription?.updatedAt || '') || 0)
    ? latest : null;

  const expired = state.status === POS_SUBSCRIPTION_STATUS.EXPIRED;
  const expiring = state.status === POS_SUBSCRIPTION_STATUS.EXPIRING;
  if (!expired && !expiring && !pending && !rejected) return null;

  const planLabel = subscription?.isTrial ? 'free trial' : `${subscription?.planName || ''} subscription`;
  // With the club's timezone, since that is the clock the end date runs on.
  const endsOn = formatPlanDateInZone(subscription?.endDate, client?.timezone);

  // ONE LINE. The banner sits above every screen, so a two-line warning cost
  // the till a row of products all day; the date and the ask now share it.
  let headline = '';
  if (expired) headline = `Your ${planLabel} expired on ${endsOn}.`;
  else if (expiring && state.daysLeft === 0) headline = `Your ${planLabel} ends today.`;
  else if (expiring) headline = `Your ${planLabel} ends in ${state.daysLeft} day${state.daysLeft === 1 ? '' : 's'} (${endsOn}).`;

  let detail = '';
  if (pending) {
    detail = `Your application for ${pending.planName} (${formatMoney(pending.total, config)}) is being reviewed — usually within ${POS_SUBSCRIPTION_REVIEW_WINDOW}.`;
  } else if (rejected) {
    // The reason is typed by an administrator and often ends in its own full
    // stop; trimmed so the sentence does not end in two.
    detail = `Your last application was not approved: ${String(rejected.rejectReason || 'no reason was given').trim().replace(/[.\s]+$/, '')}.`;
  } else {
    detail = expired
      ? 'Subscribe to keep using this till.'
      : 'Renew now to avoid a gap.';
  }

  // Red once expired, or with fewer than POS_SUBSCRIPTION_URGENT_DAYS left;
  // amber for the rest of the warning window.
  const urgent = expiring && state.daysLeft < POS_SUBSCRIPTION_URGENT_DAYS;
  const tone = (expired || urgent) ? 'bad' : (expiring ? 'warn' : (rejected ? 'bad' : 'info'));
  const canPay = can('manageConfig');

  return (
    <>
      <div className={`ps-banner ps-banner-${tone}`} role={tone === 'bad' ? 'alert' : 'status'}>
        <div className="ps-banner-inner">
          {/* Laid out as a notice strip: a solid tag and the message on the
              left (one line, truncated with an ellipsis on a narrow screen,
              full sentence in the tooltip), the Subscribe button on the right. */}
          <span className="ps-banner-tag">Important</span>
          <p className="ps-banner-text" title={`${headline} ${detail}`.trim()}>
            {headline && <strong>{headline}</strong>}
            {headline ? ' ' : ''}
            <span>{detail}</span>
          </p>
          {!pending && (canPay ? (
            <button type="button" className="ps-banner-btn" onClick={() => setOpen(true)}>Subscribe</button>
          ) : (
            <span className="ps-banner-note">Please let your manager know.</span>
          ))}
        </div>
      </div>
      <PosSubscribeModal isOpen={open} onClose={() => setOpen(false)} />
    </>
  );
}

export default PosSubscriptionBanner;
